import { useState, useEffect } from"react";
import { useTranslation } from"react-i18next";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { FileText, Plus, Clock, CheckCircle, XCircle, Calendar } from"lucide-react";
import { toast } from"sonner";

const MyOffersPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [dashboard, setDashboard] = useState(null);
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [projects, setProjects] = useState([]);
 const [phaseTasks, setPhaseTasks] = useState([]);
 const [loadingTasks, setLoadingTasks] = useState(false);
 const [formData, setFormData] = useState({
 project_id:"",
 service_type:"",
 description:"",
 amount:"",
 timeline:""
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 try {
 const [dashboardRes, projectsRes] = await Promise.all([
 axios.get("/dashboard/contractor"),
 axios.get("/projects")
 ]);
 setDashboard(dashboardRes.data);
 setProjects(projectsRes.data);
 
 // Pre-fill partner info
 if (dashboardRes.data?.partner) {
 setFormData(prev => ({
 ...prev,
 service_type: dashboardRes.data.partner.specialization ||""
 }));
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const loadProjectTasks = async (projectId) => {
 if (!projectId) {
 setPhaseTasks([]);
 return;
 }
 setLoadingTasks(true);
 try {
 const response = await axios.get(`/project-progress/${projectId}/jobs`);
 setPhaseTasks(response.data || []);
 } catch (error) {
 console.error("Failed to load project tasks", error);
 setPhaseTasks([]);
 } finally {
 setLoadingTasks(false);
 }
 };

 const handleProjectChange = (projectId) => {
 setFormData(prev => ({ ...prev, project_id: projectId, service_type:""}));
 loadProjectTasks(projectId);
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!formData.project_id || !formData.service_type || !formData.amount) {
 toast.error(t("validation.required"));
 return;
 }

 setSubmitting(true);
 try {
 await axios.post("/contractor/offers", {
 project_id: formData.project_id,
 service_type: formData.service_type,
 description: formData.description,
 amount: parseFloat(formData.amount),
 timeline: formData.timeline
 });
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 setFormData({
 project_id:"",
 service_type: dashboard?.partner?.specialization ||"",
 description:"",
 amount:"",
 timeline:""
 });
 setPhaseTasks([]);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to submit offer");
 } finally {
 setSubmitting(false);
 }
 };

 const getStatusIcon = (status) => {
 switch (status) {
 case"accepted":
 return <CheckCircle className="w-5 h-5 text-green-500"/>;
 case"rejected":
 return <XCircle className="w-5 h-5 text-red-500"/>;
 default:
 return <Clock className="w-5 h-5 text-yellow-500"/>;
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case"accepted":
 return"bg-emerald-500/10 text-emerald-700 border-green-200/60";
 case"rejected":
 return"bg-rose-500/10 text-rose-700 border-red-200/60";
 default:
 return"bg-amber-500/10 text-amber-700 border-yellow-200/60";
 }
 };

 const formatDate = (dateStr) => {
 if (!dateStr) return null;
 return new Date(dateStr).toLocaleDateString('en-GB', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 });
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-slate-600">Loading...</div>
 </div>
 </Layout>
 );
 }

 // Group tasks by phase for the dropdown
 const tasksByPhase = phaseTasks.reduce((acc, task) => {
 const phase = task.phase || 0;
 if (!acc[phase]) acc[phase] = [];
 acc[phase].push(task);
 return acc;
 }, {});

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">
 My Offers
 </h1>
 <p className="text-slate-600">
 View and submit offers for {dashboard?.partner?.company_name}
 </p>
 </div>
 <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 <Plus className="w-4 h-4 mr-2"/>
 New Offer
 </Button>
 </div>

 {/* Partner Info */}
 {dashboard?.partner && (
 <Card className="bg-blue-50/60">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <h4 className="font-semibold text-slate-900">{dashboard.partner.company_name}</h4>
 <p className="text-sm text-slate-500">{dashboard.partner.specialization}</p>
 <p className="text-[11px] text-slate-500">{dashboard.partner.email}</p>
 </div>
 <Badge variant="outline"className="bg-blue-500/10 text-blue-700">
 {dashboard?.my_offers?.length || 0} Offers
 </Badge>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Offers List */}
 {dashboard?.my_offers && dashboard.my_offers.length > 0 ? (
 <div className="space-y-4">
 {dashboard.my_offers.map((offer) => (
 <Card key={offer.id} className="">
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-4">
 {getStatusIcon(offer.status)}
 <div>
 <h3 className="font-semibold text-slate-900">{offer.service_type}</h3>
 <p className="text-sm text-slate-600 mt-1">{offer.description}</p>
 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 mt-2">
 <span>Submitted: {formatDate(offer.created_at)}</span>
 {offer.validity_date && (
 <span className="flex items-center gap-1">
 <Calendar className="w-3 h-3"/>
 Valid until: {formatDate(offer.validity_date)}
 </span>
 )}
 {offer.timeline && <span>Timeline: {offer.timeline}</span>}
 </div>
 {offer.task_ids && offer.task_ids.length > 1 && (
 <p className="text-xs text-blue-600 mt-1">
 Bundled offer: {offer.task_ids.length} tasks included
 </p>
 )}
 </div>
 </div>
 <div className="text-right">
 <p className="text-xl font-bold text-slate-900">
 €{offer.amount?.toLocaleString()}
 </p>
 <Badge variant="outline"className={getStatusColor(offer.status)}>
 {offer.status}
 </Badge>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 ) : (
 <Card className="">
 <CardContent className="text-center py-12">
 <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600 mb-4">You haven't submitted any offers yet</p>
 <Button onClick={() => setDialogOpen(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 Submit Your First Offer
 </Button>
 </CardContent>
 </Card>
 )}

 {/* New Offer Dialog */}
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogContent className="max-w-lg">
 <DialogHeader>
 <DialogTitle>Submit New Offer</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 {/* Partner Info (Read-only) */}
 <div className="p-3 bg-white/50 rounded-sm">
 <p className="text-sm text-slate-600">Submitting as:</p>
 <p className="font-semibold text-slate-900">{dashboard?.partner?.company_name}</p>
 <p className="text-[11px] text-slate-500">{dashboard?.partner?.email}</p>
 </div>

 {/* Project Selection */}
 <div>
 <Label htmlFor="project">Project *</Label>
 <Select 
 value={formData.project_id} 
 onValueChange={handleProjectChange}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select a project"/>
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 {/* Service Type / Task Selection */}
 <div>
 <Label htmlFor="service_type">Service / Task *</Label>
 {loadingTasks ? (
 <div className="p-2 text-sm text-slate-500">Loading tasks...</div>
 ) : phaseTasks.length > 0 ? (
 <Select 
 value={formData.service_type} 
 onValueChange={(val) => setFormData(prev => ({ ...prev, service_type: val }))}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select a task from the project"/>
 </SelectTrigger>
 <SelectContent>
 {Object.entries(tasksByPhase).map(([phase, tasks]) => (
 <div key={phase}>
 <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-white/60">
 Phase {phase}
 </div>
 {tasks.map((task) => (
 <SelectItem key={task.id} value={task.job_name}>
 {task.job_name}
 </SelectItem>
 ))}
 </div>
 ))}
 </SelectContent>
 </Select>
 ) : formData.project_id ? (
 <div className="space-y-2">
 <p className="text-[11px] text-slate-500">No tasks found. Enter manually:</p>
 <Input
 value={formData.service_type}
 onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value }))}
 placeholder="e.g., Urban Planning Documents"
 />
 </div>
 ) : (
 <Input
 value={formData.service_type}
 onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value }))}
 placeholder="Select a project first"
 disabled
 />
 )}
 </div>

 {/* Amount */}
 <div>
 <Label htmlFor="amount">Amount (€) *</Label>
 <Input
 type="number"
 value={formData.amount}
 onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
 placeholder="e.g., 50000"
 />
 </div>

 {/* Timeline */}
 <div>
 <Label htmlFor="timeline">Timeline</Label>
 <Input
 value={formData.timeline}
 onChange={(e) => setFormData(prev => ({ ...prev, timeline: e.target.value }))}
 placeholder="e.g., 3 months"
 />
 </div>

 {/* Description */}
 <div>
 <Label htmlFor="description">{t("common.description")}</Label>
 <Textarea
 value={formData.description}
 onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
 placeholder="Describe your offer..."
 rows={3}
 />
 </div>

 <div className="flex gap-3 justify-end">
 <Button type="button"variant="outline"onClick={() => setDialogOpen(false)}>
 Cancel
 </Button>
 <Button type="submit"disabled={submitting} className="bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 {submitting ?"Submitting...":"Submit Offer"}
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default MyOffersPage;
